import { api } from '../../lib/api';
import type { ListQuery, PagedResult } from '../administration/users/userTypes';
import type {
  CashCatalogEntry,
  CashCatalogOption,
  CashCatalogRequest,
  CashCatalogSlug,
} from './cashCatalogTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Sent whenever the key is present, empty string included — "none of these" matches no row.
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const base = (catalog: CashCatalogSlug) => `/api/cash-catalogs/${catalog}`;

export const cashCatalogsApi = {
  list: (catalog: CashCatalogSlug, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<CashCatalogEntry>>(`${base(catalog)}${toQueryString(query)}`, { signal }),

  get: (catalog: CashCatalogSlug, id: string, signal?: AbortSignal) =>
    api<CashCatalogEntry>(`${base(catalog)}/${id}`, { signal }),

  options: (catalog: CashCatalogSlug, signal?: AbortSignal) =>
    api<CashCatalogOption[]>(`${base(catalog)}/options`, { signal }),

  create: (catalog: CashCatalogSlug, body: CashCatalogRequest) =>
    api<CashCatalogEntry>(base(catalog), { method: 'POST', body }),

  update: (catalog: CashCatalogSlug, id: string, body: CashCatalogRequest) =>
    api<CashCatalogEntry>(`${base(catalog)}/${id}`, { method: 'PUT', body }),

  remove: (catalog: CashCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}`, { method: 'DELETE' }),

  restore: (catalog: CashCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}/restore`, { method: 'POST' }),

  clear: (catalog: CashCatalogSlug) => api<number>(`${base(catalog)}/clear`, { method: 'POST' }),
};
